import { Injectable } from "@angular/core";
import { AppConfig } from "src/app/shared/config";
import { FNOL_COUNTRY } from "../config/fnol-constants";

interface AdobeAnalyticsBasePayload {
  site: {
    country?: string;
    language?: string;
    platform?: string;
    environment?: string;
    code?: string;
    lob?: string;
  };
  form: {
    formName?: string,
    formType?: string,
    formStepName?: string
  },
  cta: {
    ctaName: string, // This is dimension
    ctaType: string,
    ctaURL: string
  }
  error: {
    errorCode: string;
    errorDesc: string;
    errorType: string;
  };
  page: {
    name: string;
    URL?: string;
    fullURL?: string;
    previousURL?: string;
  };
  user: {
    userRole: string;
    userId: string;
    userName: string;
  };
  fnol: {
    policyHolder?: "Yes" | "No";
    relationship?: string;
    relationshipOthers?: string;
    ClaimType?: string;
  };
}

interface AdobePageViewPayload extends AdobeAnalyticsBasePayload {
  event: string;
}


@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private previousPageURL = "";
  page: {
    name: string,
    URL: string,
    fullURL: string,
    previousURL: string
  }
  fnol: {
    policyHolder?: "Yes" | "No";
    relationship?: string;
    relationshipOthers?: string;
    ClaimType?: string;
  }
  lob: string;
  country: any;

  constructor() {
    this.country = this.getCountry();
  }

  addAnalyticsURL(lang: string): Promise<any> {
    lang = lang?.toLowerCase();
    return new Promise((resolve, reject) => {
      const normalizedLang = (lang || "").toLowerCase();
      const analyticsUrl = normalizedLang.includes("jp")
        ? AppConfig.analyticsURLJP
        : AppConfig.analyticsURL;

      if (!analyticsUrl) {
        resolve("Analytics URL not configured");
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.src = analyticsUrl;
      script.type = "text/javascript";
      script.addEventListener("load", resolve);
      script.addEventListener("error", () => reject("Error loading script"));
      script.addEventListener("abort", () => reject("Script load aborted"));
      document?.head?.appendChild(script);
    });
  }

  /* Splunk - Realtime User Monitoring initialization
     *userDetails   = claimNumber for NZ, AU & Norway
                    = email for JP
                    = policyHolder name for US
                    = fname + lname for SG
  */
  setSplunkRUMprops(userDetails) {
    const country = this.getCountry();
    this.enableSplunk(country, userDetails);
  }

  enableSplunk(country, userDetails) {
    try {
      const token = AppConfig['rumAccessToken'];
      const country = this.getCountry();
      const env = this.getEnv();
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.id = "splunkRum";
      script.innerText = ` SplunkRum.init({
          realm: 'us0',
          rumAccessToken: '` + token + `',
          applicationName: "5353-ClaimsDigitalPortal",
          version: "15.2.10",
          deploymentEnvironment: '` + env + `',
          globalAttributes: {
            'userId' : ` + JSON.stringify(userDetails) + `,
            'countryCode' : ` + JSON.stringify(country) + `
          }
        });
        SplunkSessionRecorder.init({
          realm: 'us0',
          rumAccessToken: '` + token + `'
        });`;
      document.head.appendChild(script);
    } catch (err) {
      console.log("Splunk initialization error", err);
    }
  }

  getEnv() {
    let currentEnv = '';
    if (location.hostname.includes("dev.cdpfnol.nprd.aig.com") || location.hostname.includes("localhost") || location.hostname.includes("dev2.cdpfnol.nprd.aig.com") ||
      location.hostname.includes("songai-serviced.aig")) {
      currentEnv = "DEV";
    } else if (location.hostname.includes("qa.cdpfnol.nprd.aig.com") || location.hostname.includes("qa2.cdpfnol.nprd.aig.com") || location.hostname.includes("songai-serviceq.aig.co.jp")) {
      currentEnv = "QA";
    } else if (location.hostname.includes("uat.cdpfnol.nprd.aig.com") || location.hostname.includes("songai-serviceu.aig.co.jp")) {
      currentEnv = "MODEL";
    } else if (location.hostname.includes("cdpfnol.aig.com") || location.hostname.includes("songai-service.aig.co.jp")) {
      currentEnv = "PROD";
    } else {
      currentEnv = "DEV";
    }
    return currentEnv;
  }

  getCountry() {
    const path = window.location.pathname;
    const region = path.match('jp') || path.match('singapore') || path.match('sg') || path.match('no');
    const queryParams = new URLSearchParams(window.location.search);
    const queryParam = queryParams.get("country");
    this.lob = queryParams.get("lob") || '';
    let currentCountry: any = '';

    if (region && region.length) {
      currentCountry = region[0];
    } else if (queryParam) {
      currentCountry = queryParam;
    }
    return currentCountry;
  }

  buildAdobePageViewPayload(payload: Partial<AdobePageViewPayload> = {}): Partial<AdobePageViewPayload> {

    const currentURL = window.location.href;
    const pageURLWithoutQuery = `${window.location.origin}${window.location.pathname}`;
    const { event, fnol, site, page, error, form, cta } = payload;
    if (this.fnol || fnol) {
      this.fnol = { ...this.fnol, ...fnol };
    }
    this.page = {
      ...this.page, ...page, ...{
        ...(page ? page : {}),
        URL: pageURLWithoutQuery,
        fullURL: currentURL,
        previousURL: this.previousPageURL,

      }
    };
    return {

      event: event,
      ...(error ? { error } : {}),
      form: {
        ...form,
        formName: `New Claim ${FNOL_COUNTRY[this.country?.toUpperCase()]?.countryCode || this.country}`,
        formType: "New Claim",

      },
      ...(cta ? { cta } : {}),
      site: {
        country: this.country || "",
        language: "en",
        platform: "web",
        code: FNOL_COUNTRY[this.country?.toUpperCase()]?.countryCode || this.country,
        lob: this.lob,
        environment: this.getEnv(),
        ...(site ? site : {}),
      },
      page: this.page,

      ...(this.fnol ? { fnol: this.fnol } : {}),


    };
  }

  pushAdobePageView(payload: Partial<AdobePageViewPayload> = {}) {
    console.log("Adobe Page View with event:", payload);
    window["adobeDataLayer"] = window["adobeDataLayer"] || [];
    const dataLayer = (window as any).adobeDataLayer;


    const finalPayload = this.buildAdobePageViewPayload(payload);
    dataLayer.push(finalPayload);
    this.previousPageURL = finalPayload.page.fullURL;
  }

  logDocUploadFail = (error) => {
    this.pushAdobePageView(
      this.buildAdobePageViewPayload(
        {
          event: "siteError",
          error: error
        }
      )
    );
  }

}

